import {
  Squares2X2Icon,
  RectangleGroupIcon,
  ClipboardDocumentListIcon,
  ComputerDesktopIcon,
  ChartBarIcon,
  CpuChipIcon,
  ClockIcon,
  BellAlertIcon,
  TableCellsIcon,
  TvIcon,
  FilmIcon,
  FaceSmileIcon,
  UserIcon,
  UsersIcon,
  ExclamationTriangleIcon,
  TruckIcon,
  ShieldCheckIcon,
  HandRaisedIcon,
  ArrowTrendingDownIcon,
  FireIcon,
  FolderIcon,
  LockClosedIcon,
  SignalIcon,
  AdjustmentsHorizontalIcon,
  AdjustmentsVerticalIcon,
  UserGroupIcon,
  IdentificationIcon,
  QuestionMarkCircleIcon,
  DocumentChartBarIcon,
  MapIcon,
  Cog6ToothIcon,
  ServerStackIcon,
} from '@heroicons/react/24/outline';
import type { NavNode } from '@trinetra/shared';

/**
 * Cloud sidebar: ONE scope's menu at a time, chosen by the SiteSwitcher in the title bar.
 *
 * The two trees below are never on screen together. That is the whole point — the old sidebar
 * stacked both scopes in one scrolling column, so "Alert Board" meant all-sites or one site
 * depending on which half you were reading, and the only thing distinguishing them was a
 * `GLOBAL` label that scrolled away. See constants/scope.ts.
 *
 * Because only one scope renders, nothing needs a "Multi-Site" prefix any more: the title bar
 * says which scope you are in, permanently. The URLs still have to say it — two scopes have a
 * "Live Wall" and an "Alert Board", and one path can only be one page — so the all-sites twins
 * live under an `/all-sites/` prefix and the per-site ones keep the bare path.
 *
 * The per-site tree mirrors the local dashboard's groups exactly, so operators moving between
 * the two products see one structure.
 *
 * Permission keys are the CLOUD catalog's and differ from local for several
 * surfaces (`discovery.view`, `ai-models.*`, `users.edit` for access control).
 */

/** Scope = ALL SITES. Estate-wide surfaces; none of these touch a node's tunnel. */
export const ALL_SITES_NAV: NavNode[] = [
  { name: 'Dashboard', to: '/', icon: Squares2X2Icon, end: true },
  { name: 'Live Wall', to: '/all-sites/live-wall', icon: TableCellsIcon, permission: 'multi-site.view' },
  // Cross-site alert board. Shows alert data across sites, so it needs BOTH
  // scopes — cross-site aggregation AND permission to see alerts at all.
  {
    name: 'Alert Board',
    to: '/all-sites/alert-board',
    icon: RectangleGroupIcon,
    allPermissions: ['multi-site.view', 'alerts.view'],
  },
];

/** Scope = one site. Every entry here is served through that site's tunnel. */
export const SITE_NAV: NavNode[] = [
  // The selected site at a glance — the same six cards its row shows on the all-sites dashboard,
  // and the same page the local product opens on. It exists so "Dashboard" means something in
  // BOTH scopes: all sites answers "which site needs me", one site answers "how is this site".
  // No permission: every card gates itself on the key that owns its data.
  { name: 'Dashboard', to: '/site-dashboard', icon: Squares2X2Icon, end: true },

  // THIS site's Live Wall — the saved camera grids that live on the node itself, which is why it
  // has a per-site entry while the all-sites tree's Live Wall (built from cloud-stored layouts
  // that may mix sites) is a different page. Same position and the same icon as the local
  // product's, because it is the same wall: an operator arranges it in the control room and
  // finds it here.
  { name: 'Live Wall', to: '/live-wall', icon: TvIcon, permission: 'live-wall.view' },

  // WATCHING the board is a root entry; everything else about alerts is the group below it. The
  // split is by what the operator is doing, not by subject matter — see the local nav, which
  // this mirrors.
  //
  // One entry, two layouts. This was two menu items — "Realtime Alert" (the honeycomb) and its
  // Map View "spatial twin" — showing the same live alerts through the same panel; they're now
  // one page with a toggle in its toolbar, and "Alert Board" is the name that covers both
  // layouts rather than just one of them. The map layout is a CHILD path of the board
  // (`/alert-board/map`), so the entry stays lit on it without an `alsoActiveFor`;
  // `anyPermission` because either key gets you in: the page hides the layout you aren't
  // entitled to.
  {
    name: 'Alert Board',
    to: '/alert-board',
    icon: RectangleGroupIcon,
    anyPermission: ['alerts.view', 'map.view'],
  },

  // Reviewing the past — the third of the three things an operator opens this product to do,
  // after watching the cameras and working the alerts, which is why it closes the run of root
  // entries rather than sitting inside camera admin as a sub-task. A single entry while
  // recording configuration lives inside the playback page; it becomes a group the day that
  // config gets a screen of its own.
  { name: 'Recordings', to: '/recordings', icon: FilmIcon, permission: 'recordings.view' },

  // The alert record and the rules that produce it. Alert Rules used to sit under AI Models,
  // which named the thing it is configured ON rather than the thing it produces — an operator
  // asking "why did this alert fire" was sent to a menu about models.
  {
    name: 'Alerts',
    icon: ClipboardDocumentListIcon,
    children: [
      // Not "AI Alerts": nothing here is non-AI, so the prefix distinguishes nothing. Not plain
      // "Alerts" either — that is the group's name, and "Alerts ▸ Alerts" says nothing about
      // which surface is the live board and which is the record.
      { name: 'Alert List', to: '/alerts', icon: BellAlertIcon, permission: 'alerts.view' },
      // `alerts.config`, not `ai-models.*` — the permission always described alerts, even while
      // the menu entry lived under models.
      { name: 'Alert Rules', to: '/alert-rules', icon: AdjustmentsVerticalIcon, permission: 'alerts.config' },
    ],
  },

  // Operation and management of cameras are one group — see the local nav for why the old
  // separate "Camera Management" menu was folded in here.
  //
  // Children keep the "Camera" prefix only where the bare word is ambiguous product-wide —
  // Groups, Access and Discovery all collide with user groups, user access and node discovery.
  {
    name: 'Cameras',
    icon: ComputerDesktopIcon,
    children: [
      // `alsoActiveFor` inherited from the removed "Edit Cameras": cloud camera detail
      // lives under /nodes/:nodeId/cameras/:cameraId, so it needs an explicit prefix
      // rather than falling out of the /cameras parent path like local's does.
      {
        name: 'Camera List',
        to: '/cameras',
        icon: TableCellsIcon,
        permission: 'cameras.view',
        alsoActiveFor: ['/nodes/'],
      },
      // Ex-"Camera Management". "Edit Cameras" stays out of the nav; its route
      // (/camera-management) is kept — the dashboard node rows still link to it.
      { name: 'Camera Groups', to: '/camera-groups', icon: FolderIcon, permission: 'groups.view' },
      { name: 'Access Control', to: '/access-control', icon: LockClosedIcon, permission: 'users.edit' },
      { name: 'Camera Discovery', to: '/discovery', icon: SignalIcon, permission: 'discovery.view' },
    ],
  },

  // `requiresModel` gates each report on the SELECTED node having that model ACTIVE — registered
  // and attached to at least one camera. A report for a model that node doesn't run can only ever
  // be empty, and the whole group disappears when none are active (filterNav drops empty groups).
  {
    name: 'Reports',
    icon: ChartBarIcon,
    children: [
      { name: 'Face Recognition', to: '/reports/face', icon: FaceSmileIcon, permission: 'ai_events.view', requiresModel: 'face' },
      { name: 'Person Detection', to: '/reports/person', icon: UserIcon, permission: 'ai_events.view', requiresModel: 'person' },
      { name: 'Person Count', to: '/reports/person-count', icon: UsersIcon, permission: 'ai_events.view', requiresModel: 'person-count' },
      { name: 'Intrusion Detection', to: '/reports/intrusion', icon: ExclamationTriangleIcon, permission: 'ai_events.view', requiresModel: 'intrusion' },
      { name: 'Vehicle Crossing', to: '/reports/vehicle-crossing', icon: TruckIcon, permission: 'ai_events.view', requiresModel: 'vehicle-crossing' },
      { name: 'PPE Violation', to: '/reports/ppe-violation', icon: ShieldCheckIcon, permission: 'ai_events.view', requiresModel: 'ppe-violation' },
      { name: 'Fall Detection', to: '/reports/fall', icon: ArrowTrendingDownIcon, permission: 'ai_events.view', requiresModel: 'fall' },
      { name: 'Fire & Smoke', to: '/reports/fire-smoke', icon: FireIcon, permission: 'ai_events.view', requiresModel: 'fire-smoke' },
      // Not an event model — heatmap reports metrics and never raises an alert, so this lists
      // per-camera coverage and opens the existing heatmap view.
      { name: 'Heatmap', to: '/reports/heatmap', icon: MapIcon, permission: 'ai_events.view', requiresModel: 'heatmap' },
      // Hidden until implemented:
      // { name: 'Theft Detection', to: '/reports/theft', icon: HandRaisedIcon, permission: 'ai_events.view' },
    ],
  },

  {
    name: 'AI Models',
    icon: CpuChipIcon,
    children: [
      { name: 'Model Registry', to: '/models', icon: CpuChipIcon, permission: 'ai-models.view' },
      { name: 'Model Assignments', to: '/model-assignments', icon: AdjustmentsHorizontalIcon, permission: 'ai-models.assign' },
      // "Alert Rules" was the third entry here; it now lives under Alerts, beside the list it
      // explains. What is left is the two surfaces genuinely about models.
    ],
  },

  {
    name: 'Attendance',
    icon: ClockIcon,
    children: [
      { name: 'Presence', to: '/attendance/presence', icon: UserGroupIcon, permission: 'attendance.view' },
      { name: 'Timeline', to: '/attendance/timeline', icon: ClockIcon, permission: 'attendance.view' },
      { name: 'Employees', to: '/attendance/employees', icon: IdentificationIcon, permission: 'attendance.view' },
      { name: 'Unknown Faces', to: '/attendance/unknown-faces', icon: QuestionMarkCircleIcon, permission: 'attendance.view' },
      { name: 'Payroll', to: '/attendance/payroll', icon: DocumentChartBarIcon, permission: 'attendance.view' },
      { name: 'Settings', to: '/attendance/config', icon: Cog6ToothIcon, permission: 'attendance.config' },
    ],
  },

  // Newly reachable from the menu. This page was deliberately kept out of the old sidebar
  // because it takes its node from the URL while the sidebar took one from the picker — the two
  // could disagree and name different sites on the same screen. With scope explicit and
  // app-wide, that conflict is gone: /system-health simply resolves to whichever site the title
  // bar names. It also stays enabled while a site is offline, because it is the one page you
  // still want when a site is down.
  //
  // Named to match local's user-menu entry for the same page. The scope is already stated by
  // the title bar, so "Site" in the label would only repeat it — and an operator moving between
  // the two products should not have to learn two words for one screen.
  { name: 'System Health', to: '/system-health', icon: ChartBarIcon, permission: 'monitoring.view', alsoActiveFor: ['/node-monitor/'] },
];

/**
 * Header avatar dropdown — platform-wide admin surfaces, PLUS anything with no counterpart in
 * the local app's sidebar. Keeping those out of SITE_NAV is what lets the two sidebars stay
 * identical: TURN Server and the platform-wide monitors exist only here, just as local's
 * Settings page exists only there.
 */
export const USER_MENU_NAV: NavNode[] = [
  { name: 'Platform Health', to: '/platform-health', icon: ChartBarIcon, permission: 'monitoring.view' },
  { name: 'TURN Server', to: '/turn-monitor', icon: ServerStackIcon, permission: 'turn.view' },
  { name: 'System Alerts', to: '/system-alerts', icon: BellAlertIcon, permission: 'system_alert.view' },
  { name: 'Users & Roles', to: '/user-management', icon: UsersIcon, anyPermission: ['users.view', 'roles.view'] },
];
